/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Divider, Stack, rem, useCombobox, useMantineTheme } from '@mantine/core';
import { useNavigate } from 'react-router-dom';

import Paths from '@fai/friendli-suite/features/routes/paths';
import type { PathsType } from '@fai/friendli-suite/features/routes/paths/default-paths';

import TeamSelect from './team-select';

const getTeamSelectNavigationTargetPaths = (teamId: string) => {
  const teamSettingsBasePath = Paths.team(teamId).teamSettings;

  return {
    billing: teamSettingsBasePath.billing,
    members: teamSettingsBasePath.members,
    settings: teamSettingsBasePath.settings,
    teams: Paths.userSettings.teams,
  };
};

const useTeamComboBox = (teamId: string) => {
  const navigate = useNavigate();

  const teamCombobox = useCombobox({
    onDropdownClose: () => teamCombobox.resetSelectedOption(),
    onDropdownOpen: (eventSource) => {
      if (eventSource === 'keyboard') {
        teamCombobox.selectActiveOption();
      } else {
        teamCombobox.updateSelectedOptionIndex('active');
      }
    },
  });

  const teamSettingsNavigatePaths: PathsType = getTeamSelectNavigationTargetPaths(teamId);

  const handleTeamOptionSubmit = (newValue: string) => {
    navigate(teamSettingsNavigatePaths[newValue] ?? Paths.team(teamId).default);
    teamCombobox.closeDropdown();
    teamCombobox.updateSelectedOptionIndex('active');
  };

  return { teamCombobox, handleTeamOptionSubmit };
};

export function TeamComboBox({
  teamId,
  teams,
  isTeamOwner,
}: {
  teamId: string;
  teams: {
    id: string;
    name: string | null;
  }[];
  isTeamOwner: boolean;
}) {
  const theme = useMantineTheme();
  const { teamCombobox, handleTeamOptionSubmit } = useTeamComboBox(teamId);

  return (
    <TeamSelect combobox={teamCombobox} handleOptionSubmit={handleTeamOptionSubmit}>
      <TeamSelect.Target combobox={teamCombobox} teamId={teamId} />
      <TeamSelect.DropDown>
        <TeamSelect.DropDown.CurrentTeamProfileSection teamId={teamId} />
        {isTeamOwner && (
          <>
            <Divider color={theme.pf.color.border.neutral.default} />
            <Stack gap={0} p={theme.pf.layout['4xs']}>
              <TeamSelect.DropDown.MenuOptions teamId={teamId} />
            </Stack>
          </>
        )}
        {teams.length > 0 && (
          <>
            <Divider color={theme.pf.color.border.neutral.default} />
            <Stack gap={0} p={theme.pf.layout['4xs']} mah={rem(178)} style={{ overflow: 'scroll' }}>
              <TeamSelect.DropDown.SwitchableTeamsSection teamId={teamId} teams={teams} />
            </Stack>
            <Divider color={theme.pf.color.border.neutral.default} />
            <Stack gap={0} p={theme.pf.layout['4xs']}>
              <TeamSelect.DropDown.AllTeamsOption />
            </Stack>
          </>
        )}
      </TeamSelect.DropDown>
    </TeamSelect>
  );
}
